"use client"

import { createContext, useContext, useState, useEffect , useCallback} from "react"
import { apiRequest } from '../utils/apiRequest';
import { useNavigate } from 'react-router-dom'

const AuthContext = createContext(undefined)

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isAuthChecked, setIsAuthChecked] = useState(false)
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [users, setUsers] = useState([])
  const [sortOrder, setSortOrder] = useState("default")
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [total,setTotal] = useState(0)
  const [searchPhrase, setSearchPhrase] = useState('')
  // { sessionId, clientName, expiresAt } while an admin is operating as a client.
  const [impersonation, setImpersonation] = useState(() => {
    try {
      const raw = localStorage.getItem("impersonation")
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  })

  const navigate = useNavigate()

  useEffect(() => {
    const storedUser = localStorage.getItem("user")
    const accessToken = localStorage.getItem("accessToken")
    const refreshToken = localStorage.getItem("refreshToken")

    if (storedUser && accessToken && refreshToken) {
      // Validate tokens on app start
      const validateAndRestore = async () => {
        const authResult = await checkAuth()
        if (authResult.isAuthenticated) {
          setUser(JSON.parse(storedUser))
          setIsAuthenticated(true)
        } else {
          // Try to refresh token
          try {
            const refreshResponse = await apiRequest({
              method: "POST",
              url: "/refresh",
              data: { refreshToken }
            });

            if (refreshResponse.status === 200) {
              const { user: newUser, accessToken: newToken, refreshToken: newRefreshToken } = refreshResponse.data;

              // Update storage with new tokens
              localStorage.setItem("user", JSON.stringify(newUser))
              localStorage.setItem("accessToken", newToken)
              localStorage.setItem("refreshToken", newRefreshToken)

              // Update state
              setUser(newUser)
              setIsAuthenticated(true)
              return
            }
          } catch (refreshError) {
            console.error("Token refresh failed:", refreshError)
          }

          // If validation and refresh both fail, clear auth
          localStorage.removeItem("user")
          localStorage.removeItem("accessToken")
          localStorage.removeItem("refreshToken")
          setUser(null)
          setIsAuthenticated(false)
        }
      }

      validateAndRestore().finally(() => {
        setIsAuthChecked(true)
        setLoading(false)
      })
    } else {
      setIsAuthenticated(false)
      setIsAuthChecked(true)
      setLoading(false)
    }
  }, [])

  const login = async (credentials) => {
    try {
      setLoading(true)
      const response = await apiRequest({
        method: "POST",
        url: "/login",
        data: credentials,
      });

      if (response.status === 200) {
        const { user, accessToken, refreshToken } = response.data;
        
        // Store auth data
        localStorage.setItem("user", JSON.stringify(user))
        localStorage.setItem("accessToken", accessToken)
        localStorage.setItem("refreshToken", refreshToken)
        localStorage.setItem("last_order_no", "")
        
        // Update state
        setUser(user)
        setIsAuthenticated(true)
        setIsAuthChecked(true)
        navigate("/")
        return true
      }
    } catch (error) {
      console.error("Login failed:", error)
      return false
    } finally{
      setLoading(false)
    }
  }

  // ── Admin "operate as client" (impersonation) ──────────────────────────────
  // The admin's own credentials are stashed under admin_* keys and the client token
  // takes over the normal accessToken slot, so every existing apiRequest call is
  // automatically scoped to the client with no per-call changes.
  //
  // Critically, NO refreshToken is stored while impersonating. If the admin's refresh
  // token were left in place, an expired impersonation token would trigger the refresh
  // path in apiRequest and silently mint a fresh ADMIN token — replaying the pending
  // request as the admin. See the guard in utils/apiRequest.js.
  const startImpersonation = async (userId, reason) => {
    const res = await apiRequest({
      method: "post",
      url: `/admin/impersonate/${userId}`,
      data: { reason },
    })

    const { accessToken, client, session } = res.data

    // Stash the admin session so Exit can restore it without re-login.
    localStorage.setItem("admin_user", localStorage.getItem("user") || "")
    localStorage.setItem("admin_accessToken", localStorage.getItem("accessToken") || "")
    localStorage.setItem("admin_refreshToken", localStorage.getItem("refreshToken") || "")

    localStorage.setItem("accessToken", accessToken)
    localStorage.removeItem("refreshToken")
    localStorage.setItem("user", JSON.stringify(client))
    localStorage.setItem("impersonation", JSON.stringify({
      sessionId: session.id,
      clientName: [client.first_name, client.last_name].filter(Boolean).join(" ") || client.email,
      expiresAt: session.expires_at,
    }))

    setUser(client)
    setImpersonation({
      sessionId: session.id,
      clientName: [client.first_name, client.last_name].filter(Boolean).join(" ") || client.email,
      expiresAt: session.expires_at,
    })
    return client
  }

  /**
   * Restore the admin session. `notifyServer` is false when the impersonation token
   * is already dead (expired/revoked) — there is nothing to end server-side and the
   * call would only 401.
   */
  const stopImpersonation = useCallback(async (notifyServer = true) => {
    if (notifyServer) {
      try {
        await apiRequest({ method: "post", url: "/impersonate/end", suppressNotifications: true })
      } catch {
        // Session may have already lapsed server-side — restoring locally is still correct.
      }
    }

    const adminUser = localStorage.getItem("admin_user")
    const adminToken = localStorage.getItem("admin_accessToken")
    const adminRefresh = localStorage.getItem("admin_refreshToken")

    localStorage.removeItem("impersonation")
    localStorage.removeItem("admin_user")
    localStorage.removeItem("admin_accessToken")
    localStorage.removeItem("admin_refreshToken")

    if (adminToken) {
      localStorage.setItem("accessToken", adminToken)
      if (adminRefresh) localStorage.setItem("refreshToken", adminRefresh)
      if (adminUser) localStorage.setItem("user", adminUser)
      setUser(adminUser ? JSON.parse(adminUser) : null)
      setIsAuthenticated(true)
    } else {
      // No admin session to go back to — fall back to a clean logged-out state.
      localStorage.removeItem("accessToken")
      localStorage.removeItem("refreshToken")
      localStorage.removeItem("user")
      setUser(null)
      setIsAuthenticated(false)
    }
    setImpersonation(null)
  }, [])

  const logout = async () => {
    // Exiting while impersonating must restore the admin, not log everyone out.
    if (localStorage.getItem("impersonation")) {
      await stopImpersonation(true)
      navigate("/admin/users")
      return
    }
    try {
      setLoading(true)
      const refreshToken = localStorage.getItem("refreshToken")
      
      // Attempt server-side logout
      await apiRequest({
        method: "POST",
        url: "/logout",
        data: { refreshToken }
      });
    } catch (error) {
      console.error("Logout failed:", error)
    } finally {
      // Clear client-side state regardless of server response
      localStorage.removeItem("user")
      localStorage.removeItem("accessToken")
      localStorage.removeItem("refreshToken")
      setUser(null)
      setIsAuthenticated(false)
      setLoading(false)
      navigate("/")
    }
  }

  const updateProfile = async (profile) => {
     try {
      setLoading(true)

      const response = await apiRequest({
        method: "PUT",
        url: "/profile",
        data: profile,
      });

    }catch(ex){
      console.log(ex)
    } finally{
      setLoading(false)
    }
  }

  const changePassword = async (passwords) => {
     try {
      setLoading(true)

      const response = await apiRequest({
        method: "PUT",
        url: "/security",
        data: passwords,
      });

    }catch(ex){
      console.log(ex)
    } finally{
      setLoading(false)
    }
  }

  const createUser = async (userData) => {
    try {
      setLoading(true)
      const response = await apiRequest({
        method: "POST",
        url: "/register",
        data: userData,
      });

      if (response.status === 201) {
          getUsers();
      }

      return response.data;
    } catch (error) {
      console.error("Error:", error);
      return { success: false, error };
    } finally {
      setLoading(false)
    }
  }

  const register = async (userData) => {
    try {
      setLoading(true)
      const response = await apiRequest({
        method: "POST",
        url: "/register",
        data: userData,
      });
      return response
    } catch (error) {
      console.error("Registration failed:", error)
      return { success: false, error }
    } finally{
      setLoading(false)
    }
  }

  const sendResetLink = async (email) => {
    try {
      setLoading(true)
      const response = await apiRequest({
        method: "GET",
        url: `/security?email=${email}`,
      });
      return response.data;
    } catch (error) {
      console.error("Sending password reset link failed:", error)
      return { success: false, error }
    } finally{
      setLoading(false)
    }
  }

  const resetPassword = async (newPassword, confirmPassword, token) => {
    try {
      setLoading(true)
      const response = await apiRequest({
        method: "POST",
        url: `/security`,
        data: { token, newPassword , confirmPassword}
      });
      return response;
    } catch (error) {
      console.error("Sending password reset link failed:", error)
      const message = error?.response?.data?.error || error?.response?.data?.message || error?.message
      return { success: false, error, message }
    } finally{
      setLoading(false)
    }
  }

  const sendVerifyEmail = async () => {
     try {
      setLoading(true)
      const response = await apiRequest({
        method: "GET",
        url: `/verify?email=${user.email}`,
      });
      return response;
    } catch (error) {
      console.error("Sending verification link failed:", error)
      return { success: false, error }
    } finally{
      setLoading(false)
    }
  }

  const verifyEmail = async (token) => {
     try {
      setLoading(true)
      const response = await apiRequest({
        method: "POST",
        url: `/verify`,
        data: {token}
      });
      return response;
    } catch (error) {
      console.error("Email verification failed:", error)
      return { success: false, error }
    } finally{
      setLoading(false)
    }
  }

  const getUsers = useCallback(async (s) => {
    try {
      setLoading(true)
      let url = `/users`;
      const params = new URLSearchParams();
      if (pageSize && !isNaN(pageSize) && pageSize > 0) {params.append('limit', pageSize)}
      if (currentPage) { params.append('page', currentPage);}
      if (s) { params.append('search', s);}
      url += `?${params.toString()}`;

      const response = await apiRequest({
        method: "get",
        url,
      });
      if (response.data) {
        setUsers(response.data.data);
        setTotal(response.data.pagination.total)
        setPageSize(response.data.pagination.perpage)
      }
      return response;
    } catch (error) {
      console.error(error);
      return { success: false, error };
    } finally {
      setLoading(false)
    }
  }, []);

 const checkAuth = async () => {
    try {
      setLoading(true)
      const user = localStorage.getItem("user");
      const token = localStorage.getItem("accessToken");

      if (!user || !token) {
        setIsAuthenticated(false);
        setUser(null);
        return { user: null, isAuthenticated: false };
      }

      // Decode the token to check expiration
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiryTime = payload.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();

      // Check if token is expired
      if (currentTime > expiryTime) {
        // Token expired, clear storage
        localStorage.removeItem("user");
        localStorage.removeItem("accessToken");
        setIsAuthenticated(false);
        setUser(null);
        return { user: null, isAuthenticated: false };
      }

      // Token is valid
      return { user: JSON.parse(user), isAuthenticated: true };
    } catch (error) {
      console.error("Error checking authentication:", error);
      // If token is malformed or invalid, treat as unauthenticated
      setIsAuthenticated(false);
      setUser(null);
      return { user: null, isAuthenticated: false };
    } finally {
      setLoading(false)
    }
  };

  return (
    <AuthContext.Provider value={
      {
         isAuthenticated,
         isAuthChecked,
         user,loading,
         login, register, createUser, users, getUsers, searchPhrase, setSearchPhrase, logout,
         checkAuth, changePassword, updateProfile, sendResetLink, resetPassword, verifyEmail, sendVerifyEmail,
         impersonation, startImpersonation, stopImpersonation
      }}>{children}</AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
